import React, { useEffect, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { useNavigate, useParams } from "react-router-dom";
import { db } from "../firebase/firebaseConfig";
import {
  formatReportDate,
  normalizeDetailItems,
  normalizeReport,
  toDate,
} from "./despatchViewHelper";

function detailErrorMessage(error) {
  if (error?.code === "permission-denied") {
    return "Access denied. Your account needs the admin or despatch role.";
  }
  return error?.message
    ? `Could not load this report: ${error.message}`
    : "Could not load this report. Please try again.";
}

function auditIdentity(identity) {
  if (!identity || typeof identity !== "object") return "Not recorded";
  return identity.email || identity.uid || "Not recorded";
}

function auditDate(value) {
  const date = toDate(value);
  return date ? date.toLocaleString("en-GB") : "Not recorded";
}

export default function DespatchDetailView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [summaryExists, setSummaryExists] = useState(false);
  const [details, setDetails] = useState([]);
  const [detailExists, setDetailExists] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");

    Promise.all([
      getDoc(doc(db, "despatchreports", id)),
      getDoc(doc(db, "despatchreportsdetailed", id)),
    ])
      .then(([summarySnapshot, detailSnapshot]) => {
        if (cancelled) return;

        setSummaryExists(summarySnapshot.exists());
        setDetailExists(detailSnapshot.exists());
        setSummary(
          summarySnapshot.exists() ? normalizeReport(summarySnapshot) : null
        );
        setDetails(
          detailSnapshot.exists()
            ? normalizeDetailItems(detailSnapshot)
            : []
        );
      })
      .catch((loadError) => {
        if (cancelled) return;
        console.error("Error fetching despatch details:", loadError);
        setError(detailErrorMessage(loadError));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [id]);

  const damaged = !summaryExists || !detailExists || !summary?.createdAt;
  const summaryData = summary?._raw || {};
  const hasAuditMetadata = [
    "createdBy",
    "completedBy",
    "completedAt",
    "itemCount",
  ].some((field) =>
    Object.prototype.hasOwnProperty.call(summaryData, field)
  );

  return (
    <main className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-5xl space-y-5">
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              Despatch Report Details
            </h1>
            <p className="mt-1 break-all text-sm text-gray-500">
              Document ID: {id}
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate("/DespatchView")}
            className="rounded bg-primary px-4 py-2 text-white hover:bg-hover"
          >
            Back to Reports
          </button>
        </header>

        {loading ? (
          <section className="rounded-lg border border-blue-200 bg-blue-50 p-6 text-center text-blue-700">
            Loading report details…
          </section>
        ) : null}

        {error ? (
          <section
            role="alert"
            className="rounded-lg border border-red-300 bg-red-50 p-4 text-red-800"
          >
            <div className="font-semibold">Report unavailable</div>
            <div className="mt-1 text-sm">{error}</div>
          </section>
        ) : null}

        {!loading && !error ? (
          <>
            <section className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                <div className="text-xs font-semibold uppercase text-gray-500">
                  Report number
                </div>
                <div className="mt-1 text-xl font-bold text-gray-900">
                  {summary?.reportNumber || "—"}
                </div>
              </div>
              <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                <div className="text-xs font-semibold uppercase text-gray-500">
                  Created date
                </div>
                <div className="mt-1 text-xl font-bold text-gray-900">
                  {formatReportDate(summary?.createdAt) || "—"}
                </div>
              </div>
              <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                <div className="text-xs font-semibold uppercase text-gray-500">
                  Status
                </div>
                <div className="mt-2">
                  {summaryExists ? (
                    <span
                      className={`rounded-full px-2.5 py-1 text-sm font-semibold ${
                        summary?.completed
                          ? "bg-emerald-100 text-emerald-800"
                          : "bg-amber-100 text-amber-800"
                      }`}
                    >
                      {summary?.completed ? "Completed" : "Pending"}
                    </span>
                  ) : (
                    "—"
                  )}
                </div>
              </div>
            </section>

            {hasAuditMetadata ? (
              <section className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                <h2 className="text-sm font-semibold uppercase text-gray-500">
                  Audit metadata
                </h2>
                <dl className="mt-3 grid grid-cols-1 gap-4 text-sm sm:grid-cols-2 lg:grid-cols-3">
                  {Object.prototype.hasOwnProperty.call(
                    summaryData,
                    "createdBy"
                  ) ? (
                    <div>
                      <dt className="font-medium text-gray-500">Created by</dt>
                      <dd className="mt-1 break-all text-gray-900">
                        {auditIdentity(summaryData.createdBy)}
                      </dd>
                    </div>
                  ) : null}
                  {Object.prototype.hasOwnProperty.call(
                    summaryData,
                    "createdAt"
                  ) ? (
                    <div>
                      <dt className="font-medium text-gray-500">Created at</dt>
                      <dd className="mt-1 text-gray-900">
                        {auditDate(summaryData.createdAt)}
                      </dd>
                    </div>
                  ) : null}
                  {Object.prototype.hasOwnProperty.call(
                    summaryData,
                    "completedBy"
                  ) ? (
                    <div>
                      <dt className="font-medium text-gray-500">
                        Completed by
                      </dt>
                      <dd className="mt-1 break-all text-gray-900">
                        {auditIdentity(summaryData.completedBy)}
                      </dd>
                    </div>
                  ) : null}
                  {Object.prototype.hasOwnProperty.call(
                    summaryData,
                    "completedAt"
                  ) ? (
                    <div>
                      <dt className="font-medium text-gray-500">
                        Completed at
                      </dt>
                      <dd className="mt-1 text-gray-900">
                        {auditDate(summaryData.completedAt)}
                      </dd>
                    </div>
                  ) : null}
                  {Object.prototype.hasOwnProperty.call(
                    summaryData,
                    "itemCount"
                  ) ? (
                    <div>
                      <dt className="font-medium text-gray-500">Item count</dt>
                      <dd className="mt-1 text-gray-900">
                        {summaryData.itemCount}
                      </dd>
                    </div>
                  ) : null}
                </dl>
              </section>
            ) : null}

            {damaged ? (
              <section className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-amber-900">
                <div className="font-semibold">
                  Incomplete / damaged despatch record
                </div>
                <ul className="mt-2 list-disc space-y-1 pl-5 text-sm">
                  {!summaryExists ? (
                    <li>The summary document is missing.</li>
                  ) : null}
                  {!detailExists ? (
                    <li>
                      The detail document is missing, so scanned items cannot
                      be displayed.
                    </li>
                  ) : null}
                  {summaryExists && !summary?.createdAt ? (
                    <li>The summary created date is missing or malformed.</li>
                  ) : null}
                </ul>
              </section>
            ) : null}

            {detailExists && details.length > 0 ? (
              <section className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-primary text-white">
                      <tr>
                        <th className="px-4 py-3 text-left">#</th>
                        <th className="px-4 py-3 text-left">Serial</th>
                        <th className="px-4 py-3 text-left">Product</th>
                        <th className="px-4 py-3 text-left">Type</th>
                        <th className="px-4 py-3 text-left">Full Barcode</th>
                        <th className="px-4 py-3 text-left">Scanned At</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {details.map((item, index) => (
                        <tr key={item.id || index}>
                          <td className="px-4 py-3">{index + 1}</td>
                          <td className="px-4 py-3 font-mono">
                            {item.serialNumber || "—"}
                          </td>
                          <td className="px-4 py-3">
                            {item.productCode || "—"}
                          </td>
                          <td className="px-4 py-3">
                            {item.special || "—"}
                          </td>
                          <td className="px-4 py-3 font-mono">
                            {item.barcode || "—"}
                          </td>
                          <td className="px-4 py-3">
                            {item.scannedAt
                              ? item.scannedAt.toLocaleString("en-GB")
                              : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            ) : null}

            {detailExists && details.length === 0 ? (
              <section className="rounded-lg border border-gray-200 bg-white p-8 text-center text-gray-600">
                The detail document exists but contains no scanned items.
              </section>
            ) : null}
          </>
        ) : null}
      </div>
    </main>
  );
}
